import { supabase } from '@/integrations/supabase/client';

// Create a service role client for public assessment paths
import { createClient } from '@supabase/supabase-js';
const serviceRoleClient = createClient(
  import.meta.env.VITE_SUPABASE_URL || "https://jftsemrnjqbzinphyrpm.supabase.co",
  import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY
);

export const addCredits = async (
  organizationId: string,
  amount: number,
  description?: string,
  transactionType: 'usage' | 'subscription' | 'bonus' | 'refund' | 'adjustment' = 'adjustment'
) => {
  try {
    const { data, error } = await supabase
      .rpc('add_organization_credits', {
        p_organization_id: organizationId,
        p_amount: amount,
        p_description: description,
        p_transaction_type: transactionType
      });

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const useCredits = async (
  organizationId: string,
  amount: number = 1,
  assessmentId?: string,
  description: string = 'Assessment credit usage'
) => {
  try {
    // Use negative amount for credit usage
    const { data, error } = await supabase
      .rpc('add_organization_credits', {
        p_organization_id: organizationId,
        p_amount: -amount,
        p_description: description,
        p_transaction_type: 'usage'
      });

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const getCreditsBalance = async (organizationId: string) => {
  try {
    // Determine if this is a public assessment path (candidate landing page)
    const isPublicPath = window.location.pathname.includes('/assessment') && 
                        window.location.pathname.includes('/landing');
    
    // Use service role client for public paths, regular client otherwise
    const client = isPublicPath ? serviceRoleClient : supabase;
    
    // Direct database query approach with handling for multiple records
    try {
      // First check credits table
      const { data: creditsData, error: creditsError } = await client
        .from('credits')
        .select('remaining_credits')
        .eq('organization_id', organizationId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (!creditsError && creditsData && creditsData.remaining_credits !== undefined && creditsData.remaining_credits > 0) {
        return creditsData.remaining_credits;
      }
      
      // If that fails, try history table
      const { data: historyData, error: historyError } = await client
        .from('organization_credit_history')
        .select('balance')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (!historyError && historyData && historyData.length > 0 && historyData[0].balance > 0) {
        return historyData[0].balance;
      }
      
      // No credits found in either table
      return { balance: 0 };
    } catch (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
};

export const getCreditHistory = async (userId: string) => {
  try {
    // First get the user's organization ID
    const { data: userData, error: userError } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('id', userId)
      .single();

    if (userError) throw userError;
    if (!userData?.organization_id) {
      throw new Error('No organization found for user');
    }

    // Then get credit history for the organization
    const { data, error } = await supabase
      .from('organization_credit_history')
      .select(`
        *,
        assessments (
          id,
          title
        )
      `)
      .eq('organization_id', userData.organization_id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

export const getUserCredits = async (userId: string) => {
  try {
    // First get the user's organization ID
    const { data: userData, error: userError } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('id', userId)
      .single();

    if (userError) throw userError;
    if (!userData?.organization_id) {
      throw new Error('No organization found for user');
    }

    // Then get the organization's credits and details
    // Modified to handle potential duplicate records by getting the most recent one
    const { data: creditsData, error: creditsError } = await supabase
      .from('credits')
      .select(`
        remaining_credits,
        organizations!inner (
          id,
          name
        )
      `)
      .eq('organization_id', userData.organization_id)
      .order('updated_at', { ascending: false })
      .limit(1)
      .single();

    if (creditsError) throw creditsError;

    // Get credit history for usage calculation
    const { data: historyData, error: historyError } = await supabase
      .from('organization_credit_history')
      .select('created_at, amount, transaction_type')
      .eq('organization_id', userData.organization_id)
      .order('created_at', { ascending: false })
      .limit(50);

    if (historyError) throw historyError;

    // Calculate used credits from history
    const usedCredits = historyData?.reduce((sum, record) => 
      record.amount < 0 ? sum + Math.abs(record.amount) : sum, 0) || 0;

    // Get the last reset date
    const lastReset = historyData?.find(record => record.transaction_type === 'subscription')?.created_at || null;

    const orgData = Array.isArray(creditsData?.organizations) 
      ? creditsData?.organizations[0] as { id: string; name: string }
      : null;

    return {
      balance: creditsData?.remaining_credits || 0,
      organization_id: userData.organization_id,
      organization_name: orgData?.name || null,
      last_reset_at: lastReset,
      total_used: usedCredits
    };
  } catch (error) {
    throw error;
  }
};

export const useCreditsForAssessment = async (
  userId: string,
  assessmentId: string,
  amount: number = 1,
  description: string = 'Assessment credit usage'
) => {
  try {
    // First get the user's organization ID
    const { data: userData, error: userError } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('id', userId)
      .single();

    if (userError) throw userError;
    if (!userData?.organization_id) {
      throw new Error('No organization found for user');
    }

    // Use negative amount for credit usage
    const { data, error } = await supabase
      .rpc('add_organization_credits', {
        p_organization_id: userData.organization_id,
        p_amount: -amount,
        p_description: description,
        p_transaction_type: 'usage'
      });

    if (error) throw error;

    // Record the assessment ID in credit history
    await supabase
      .from('organization_credit_history')
      .update({ assessment_id: assessmentId })
      .eq('organization_id', userData.organization_id)
      .eq('transaction_type', 'usage')
      .is('assessment_id', null)
      .order('created_at', { ascending: false })
      .limit(1);

    return data;
  } catch (error) {
    throw error;
  }
};